import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { Button, CollapsibleContainer, usePageTitle } from 'slab'
import { useGetEmployersEmployersIdOwners, usePutEmployersEmployersIdUpdateOwnerInvitationFormStatus } from 'utils/web_api'
import { type EmployersComplianceOwner } from 'utils/web_api.schemas'

import { TrustFooter } from './TrustFooter'
import { CenteredLayout } from '../employer_onboarding/CenteredLayout'
import { useEmployer } from '../employer_onboarding/EmployerProvider'

export function ConfirmDetailsPage () {
  usePageTitle('Confirm Details')
  const navigate = useNavigate()
  const { employer } = useEmployer()
  const updateOwnerInvitationFormStatus = usePutEmployersEmployersIdUpdateOwnerInvitationFormStatus()

  const [controllingOwner, setControllingOwner] = useState<EmployersComplianceOwner | null>(null)
  const [beneficialOwners, setBeneficialOwners] = useState<EmployersComplianceOwner[]>([])
  const [openCards, setOpenCards] = useState<Set<string>>(new Set())

  // Fetch existing beneficial owners
  const { data: existingOwners, isLoading: isLoadingOwners, error: ownersError } = useGetEmployersEmployersIdOwners(
    employer?.id || '',
    {
      query: {
        enabled: !!employer?.id
      }
    }
  )

  // Load controlling owner and beneficial owners
  useEffect(() => {
    if (existingOwners) {
      const controlling = existingOwners.find(owner => owner.is_controlling_officer)
      const beneficial = existingOwners.filter(owner => !owner.is_controlling_officer)

      setControllingOwner(controlling || null)
      setBeneficialOwners(beneficial)
    }
  }, [existingOwners])

  const handleBack = () => {
    if (beneficialOwners.length > 0 || controllingOwner?.is_beneficial_owner) {
      navigate('/employers/verification/owner_invitation/add-owners')
    } else {
      navigate('/employers/verification/owner_invitation/ownership-information')
    }
  }

  const handleSubmit = async () => {
    try {
      await updateOwnerInvitationFormStatus.mutateAsync({
        id: employer?.id ?? '',
        data: { status: 'submitted' }
      })
      navigate('/employers/verification/owner_invitation/complete')
    } catch (error) {
      console.error('Failed to update owner invitation form status:', error)
      toast.error('Failed to submit owner invitation form. Please try again.')
    }
  }

  const toggleCard = (ownerId: string) => {
    setOpenCards(prev => {
      const newSet = new Set(prev)
      if (newSet.has(ownerId)) {
        newSet.delete(ownerId)
      } else {
        newSet.add(ownerId)
      }
      return newSet
    })
  }

  const renderOwnerDetails = (owner: EmployersComplianceOwner, _isControllingOwner: boolean = false) => {
    const isOpen = openCards.has(owner.id)

    return (
      <div key={owner.id} className='flex flex-col gap-2'>
        <CollapsibleContainer
          heading={
            <div className='flex items-center gap-2'>
              <span className='body-bold'>{owner.first_name} {owner.last_name}</span>
            </div>
          }
          defaultOpen={false}
          open={isOpen}
          onOpenChange={() => toggleCard(owner.id)}
        >
          <dl className='flex flex-col flex-1 gap-6 p-4'>
            <div className='flex flex-row gap-4 flex-wrap'>
              <div className='flex-1 min-w-56'>
                <dt className='block body-bold text-gray-500 mb-1'>First name</dt>
                <dd className='px-3 py-2 bg-gray-50 border border-gray-200 rounded-md body text-gray-800'>
                  {owner.first_name}
                </dd>
              </div>
              <div className='flex-1 min-w-56'>
                <dt className='block body-bold text-gray-500 mb-1'>Last name</dt>
                <dl className='px-3 py-2 bg-gray-50 border border-gray-200 rounded-md body text-gray-800'>
                  {owner.last_name}
                </dl>
              </div>
            </div>
            <div className='flex flex-row gap-4 flex-wrap'>
              <div className='flex-1 min-w-56'>
                <dt className='block body-bold text-gray-500 mb-1'>Title</dt>
                <dd className='px-3 py-2 bg-gray-50 border border-gray-200 rounded-md body text-gray-800'>
                  {owner.title || '—'}
                </dd>
              </div>
            </div>
            <div className='flex flex-row gap-4 flex-wrap'>
              <div className='flex-1 min-w-56'>
                <dt className='block body-bold text-gray-500 mb-1'>Email</dt>
                <dd className='px-3 py-2 bg-gray-50 border border-gray-200 rounded-md body text-gray-800'>
                  {owner.email}
                </dd>
                <dd className='body-sm text-gray-500 mt-1'>
                  This is where we will send a message to the Owner with a request for additional information.
                </dd>
              </div>
            </div>
          </dl>
        </CollapsibleContainer>
      </div>
    )
  }

  const renderContent = () => {
    if (isLoadingOwners) {
      return (
        <CenteredLayout.Container>
          <div className='flex justify-center items-center py-8'>
            <div className='text-gray-500'>Loading details...</div>
          </div>
        </CenteredLayout.Container>
      )
    } else if (ownersError) {
      return (
        <>
          <CenteredLayout.Container>
            <div className='flex justify-center items-center py-8'>
              <div className='text-red-500'>Error loading details. Please try again.</div>
            </div>
          </CenteredLayout.Container>
          <CenteredLayout.Buttons align='center'>
            <Button
              variant='secondary'
              onClick={handleBack}
              leftIcon={ArrowLeftIcon}
              type='button'
            >
              Back
            </Button>
          </CenteredLayout.Buttons>
        </>
      )
    } else {
      return (
        <>
          <CenteredLayout.Container>
            <div className='flex flex-col gap-8'>
              {/* Your Details Section */}
              <div>
                <h3 className='text-lg font-medium text-gray-900 mb-4'>Individual with significant control</h3>
                {controllingOwner
                  ? (
                      renderOwnerDetails(controllingOwner, true)
                    )
                  : (
                    <div className='bg-gray-50 border border-gray-200 rounded-lg p-6 text-center'>
                      <p className='text-gray-500 italic'>No individual with significant control found</p>
                    </div>
                    )}
              </div>

              {/* Owners Section */}
              <div>
                <h3 className='text-lg font-medium text-gray-900 mb-4'>Additional owners</h3>
                {beneficialOwners.length > 0
                  ? (
                    <div className='space-y-4'>
                      {beneficialOwners.map((owner) => renderOwnerDetails(owner, false))}
                    </div>
                    )
                  : (
                    <div className='bg-gray-50 border border-gray-200 rounded-lg p-6 text-center'>
                      <p className='text-gray-500 italic'>No additional owners</p>
                    </div>
                    )}
              </div>
            </div>
          </CenteredLayout.Container>
          <CenteredLayout.Buttons align='center'>
            <Button
              variant='secondary'
              onClick={handleBack}
              type='button'
            >
              Back
            </Button>
            <Button
              variant='primary'
              onClick={handleSubmit}
              rightIcon={ArrowRightIcon}
              type='button'
            >
              Submit
            </Button>
          </CenteredLayout.Buttons>
          <TrustFooter />
        </>
      )
    }
  }

  return (
    <CenteredLayout>
      <CenteredLayout.Header
        heading="Here's what you've given us"
        subheading="Take a look and confirm everything looks right. Once you click Submit below, we'll send an email to each individual below to collect more information."
        align='center'
      />
      {renderContent()}
    </CenteredLayout>
  )
}
